<template>
  <!--
    @ Author Fábio Brasileiro
    Componente reutilizável de link de telefone de contato.

    - Gatilho estilo "ghost" (só borda; fundo bem suave ao passar o mouse / abrir).
    - Ao clicar, abre um menu para escolher o canal: WhatsApp Web ou WATI.
    - Cada canal tem 2 ações:
        • clicar na linha       -> abre o canal (redireciona em nova aba);
        • botão copiar ao lado  -> copia o link, mostra toast + animação e
          MANTÉM o menu aberto (pode copiar o outro canal também).
    - Tooltip (title) no hover deixa claro que o número é clicável.

    Uso:
      <contato-telefone-link :telefone="cliente.telefone" />
      <contato-telefone-link :telefone="tr.contato"><b>{{ tr.contato }}</b></contato-telefone-link>
  -->
  <el-dropdown
    v-if="temCanal"
    ref="dropdown"
    trigger="click"
    placement="bottom-start"
    :hide-on-click="false"
    class="ctl-dropdown"
    @click.native.stop
    @visible-change="aoMudarVisibilidade"
  >
    <span
      class="ctl-trigger"
      :class="{ 'is-open': aberto }"
      :title="tooltip"
      role="button"
      tabindex="0"
    >
      <svg class="ctl-trigger__phone" viewBox="0 0 24 24" width="13" height="13" aria-hidden="true">
        <path fill="currentColor" d="M6.6 10.8a15.5 15.5 0 006.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1A17 17 0 013 4c0-.6.4-1 1-1h3.4c.6 0 1 .4 1 1 0 1.2.2 2.4.6 3.6.1.4 0 .8-.3 1l-2.1 2.2z"/>
      </svg>
      <span class="ctl-trigger__text"><slot>{{ rotulo || telefone }}</slot></span>
      <svg class="ctl-trigger__chevron" viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">
        <path fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" d="M6 9l6 6 6-6"/>
      </svg>
    </span>

    <el-dropdown-menu slot="dropdown" class="ctl-menu">
      <div class="ctl-menu__label">Falar com o cliente</div>

      <div v-if="linkWhatsapp" class="ctl-item">
        <button type="button" class="ctl-item__main" @click="abrir('whatsapp')">
          <span class="ctl-item__icon ctl-item__icon--wa">
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path fill="currentColor" d="M19.05 4.91A9.82 9.82 0 0012.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 004.78 1.22h.01c5.46 0 9.9-4.45 9.9-9.91 0-2.65-1.03-5.14-2.89-7.02zM12.04 20.15h-.01a8.2 8.2 0 01-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.2 8.2 0 01-1.26-4.38c0-4.54 3.7-8.23 8.25-8.23a8.2 8.2 0 015.83 2.42 8.18 8.18 0 012.41 5.82c0 4.54-3.7 8.23-8.24 8.23zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.25-.64.8-.79.97-.14.16-.29.18-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.01-.38.11-.5.11-.11.25-.29.37-.43.13-.15.17-.25.25-.42.08-.16.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.4-.42-.56-.42l-.48-.01c-.16 0-.43.06-.66.31-.22.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.16 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.47-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.14-1.18-.06-.1-.22-.16-.47-.28z"/>
            </svg>
          </span>
          <span class="ctl-item__body">
            <span class="ctl-item__title">WhatsApp Web</span>
            <span class="ctl-item__desc">Abrir conversa</span>
          </span>
          <svg class="ctl-item__go" viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
            <path fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" d="M7 17L17 7M9 7h8v8"/>
          </svg>
        </button>
        <button
          type="button"
          class="ctl-copy"
          :class="{ 'is-copied': copiado === 'whatsapp' }"
          :title="copiado === 'whatsapp' ? 'Copiado!' : 'Copiar link do WhatsApp'"
          @click.stop="copiar('whatsapp')"
        >
          <svg v-if="copiado === 'whatsapp'" viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
            <path fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" d="M5 12.5l4.5 4.5L19 7"/>
          </svg>
          <svg v-else viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
            <path fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" d="M9 9h9a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1v-9a1 1 0 011-1z"/>
            <path fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" d="M5 15H4a1 1 0 01-1-1V5a1 1 0 011-1h9a1 1 0 011 1v1"/>
          </svg>
        </button>
      </div>

      <div v-if="linkWati" class="ctl-item">
        <button type="button" class="ctl-item__main" @click="abrir('wati')">
          <span class="ctl-item__icon ctl-item__icon--wati">
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" d="M4 5h16a1 1 0 011 1v9a1 1 0 01-1 1H9l-4 3v-3H4a1 1 0 01-1-1V6a1 1 0 011-1z"/>
              <circle cx="8.5" cy="10.5" r="1.1" fill="currentColor"/>
              <circle cx="12" cy="10.5" r="1.1" fill="currentColor"/>
              <circle cx="15.5" cy="10.5" r="1.1" fill="currentColor"/>
            </svg>
          </span>
          <span class="ctl-item__body">
            <span class="ctl-item__title">WATI</span>
            <span class="ctl-item__desc">Painel de atendimento</span>
          </span>
          <svg class="ctl-item__go" viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
            <path fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" d="M7 17L17 7M9 7h8v8"/>
          </svg>
        </button>
        <button
          type="button"
          class="ctl-copy"
          :class="{ 'is-copied': copiado === 'wati' }"
          :title="copiado === 'wati' ? 'Copiado!' : 'Copiar link do WATI'"
          @click.stop="copiar('wati')"
        >
          <svg v-if="copiado === 'wati'" viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
            <path fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" d="M5 12.5l4.5 4.5L19 7"/>
          </svg>
          <svg v-else viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
            <path fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" d="M9 9h9a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1v-9a1 1 0 011-1z"/>
            <path fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" d="M5 15H4a1 1 0 01-1-1V5a1 1 0 011-1h9a1 1 0 011 1v1"/>
          </svg>
        </button>
      </div>
    </el-dropdown-menu>
  </el-dropdown>

  <span v-else class="ctl-text">
    <slot>{{ rotulo || telefone }}</slot>
  </span>
</template>

<script>
export default {
  name: "ContatoTelefoneLink",
  props: {
    // Número de telefone do contato (com ou sem máscara).
    telefone: {
      type: [String, Number],
      default: "",
    },
    // Texto opcional exibido no lugar do número (também é possível usar o slot).
    rotulo: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      aberto: false,
      copiado: null, // 'whatsapp' | 'wati' | null
      copiadoTimer: null,
    };
  },
  computed: {
    tooltip() {
      return "Clique para escolher como falar com o cliente (WhatsApp ou WATI)";
    },
    // Base da URL do WATI vinda do ambiente (ex.: https://live.wati.io/<tenant>).
    baseWati() {
      return process.env.VUE_APP_WATI_URL || "";
    },
    // Apenas os dígitos do telefone informado.
    digitos() {
      let d = String(this.telefone || "").replace(/\D/g, "");
      if (d.length === 8 || d.length === 9) {
        d = `92${d}`;
      }
      return d;
    },
    // Número normalizado para o WhatsApp (adiciona DDI 55 quando necessário).
    numeroWhatsapp() {
      const d = this.digitos;
      if (d.length === 10 || d.length === 11) return `55${d}`;
      if (d.length === 12 || d.length === 13) return d; // já possui DDI
      return "";
    },
    // Número normalizado para o WATI (mesma regra usada no modal de confirmação).
    numeroWati() {
      let n = this.digitos;
      if (!n) return "";
      if (!n.startsWith("55")) n = `55${n}`;
      if (n.length === 13 && n[4] === "9") {
        n = n.slice(0, 4) + n.slice(5);
      }
      return n;
    },
    linkWhatsapp() {
      return this.numeroWhatsapp ? `https://wa.me/${this.numeroWhatsapp}` : "";
    },
    linkWati() {
      if (!this.baseWati || !this.numeroWati) return "";
      return `${this.baseWati}/teamInbox?search={"searchString":"${this.numeroWati}"}`;
    },
    temCanal() {
      return !!(this.linkWhatsapp || this.linkWati);
    },
  },
  beforeDestroy() {
    if (this.copiadoTimer) clearTimeout(this.copiadoTimer);
  },
  methods: {
    aoMudarVisibilidade(estaAberto) {
      this.aberto = estaAberto;
      if (!estaAberto) {
        this.copiado = null;
      }
    },
    linkDoCanal(canal) {
      return canal === "wati" ? this.linkWati : this.linkWhatsapp;
    },
    // Clicar na linha do canal: abre em nova aba e fecha o menu.
    abrir(canal) {
      const url = this.linkDoCanal(canal);
      if (url) window.open(url, "_blank", "noopener,noreferrer");
      this.fechar();
    },
    fechar() {
      this.aberto = false;
      const dd = this.$refs.dropdown;
      if (dd && typeof dd.hide === "function") dd.hide();
    },
    // Clicar no ícone copiar: copia a URL, mostra toast + animação e
    // mantém o menu aberto (a pessoa pode abrir/copiar o outro canal depois).
    async copiar(canal) {
      const url = this.linkDoCanal(canal);
      if (!url) return;
      const ok = await this.copiarTexto(url);

      this.copiado = canal;
      if (this.copiadoTimer) clearTimeout(this.copiadoTimer);
      this.copiadoTimer = setTimeout(() => {
        this.copiado = null;
      }, 1500);

      if (this.$vs && this.$vs.notify) {
        this.$vs.notify({
          time: 2500,
          title: ok ? "Link copiado!" : "Não foi possível copiar",
          text: ok
            ? `Link do ${canal === "wati" ? "WATI" : "WhatsApp"} copiado para a área de transferência`
            : "Copie o link manualmente, por favor",
          color: ok ? "success" : "danger",
          position: "bottom-right",
          icon: ok ? "content_copy" : "error",
        });
      } else if (this.$message) {
        this.$message({
          message: ok ? `Link do ${canal === "wati" ? "WATI" : "WhatsApp"} copiado!` : "Erro ao copiar",
          type: ok ? "success" : "error",
          showClose: true
        });
      }
      // menu permanece aberto de propósito (não chama fechar()).
    },
    async copiarTexto(texto) {
      if (this.$copyText) {
        try {
          await this.$copyText(texto);
          return true;
        } catch (e) {
          console.error("Falha ao copiar usando vue-clipboard2", e);
        }
      }
      
      // Fallback manual reforçado caso o plugin não consiga
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(texto);
          return true;
        }
      } catch (e) {
        // Fallback para document.execCommand
      }

      try {
        const input = document.createElement("input");
        input.value = texto;
        input.style.position = "fixed";
        input.style.top = "-10000px";
        document.body.appendChild(input);
        input.focus();
        input.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(input);
        return ok;
      } catch (e) {
        return false;
      }
    },
  },
};
</script>

<!-- Gatilho: fica no DOM do componente (scoped) -->
<style scoped>
.ctl-dropdown {
  display: inline-flex;
  vertical-align: middle;
}
.ctl-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  padding: 2px 9px;
  border-radius: 8px;
  border: 1px solid #d1d5dbbb; /* ghost: só a borda */
  background: transparent;
  color: #374151; /* gray-700 */
  font-weight: 600;
  line-height: 1.45;
  white-space: nowrap;
  outline: none;
  transition: background-color 0.15s ease, border-color 0.15s ease,
    color 0.15s ease, box-shadow 0.15s ease;
}
.ctl-trigger:hover {
  border-color: #6ee7b7; /* emerald-300 */
  color: #047857; /* emerald-700 */
  background: rgba(16, 185, 129, 0.06); /* fundo bem fraquinho */
}
.ctl-trigger.is-open {
  border-color: #34d399; /* emerald-400 */
  color: #047857;
  background: rgba(16, 185, 129, 0.1); /* fundo suave quando aberto */
  /* box-shadow: 10  0 8 0px rgba(16, 185, 129, 0.12); */
}
.ctl-trigger__phone {
  flex: 0 0 auto;
  opacity: 0.7;
}
.ctl-trigger__text {
  font-variant-numeric: tabular-nums;
}
.ctl-trigger__chevron {
  flex: 0 0 auto;
  opacity: 0.5;
  transition: transform 0.18s ease, opacity 0.15s ease;
}
.ctl-trigger.is-open .ctl-trigger__chevron {
  transform: rotate(180deg);
  opacity: 0.85;
}
.ctl-text {
  color: inherit;
}
</style>

<!-- Menu: Element UI move o popup para o <body> (sem scoped) -->
<style>
/* Força o Toast (Vuesax e Element UI) a ficar acima do dropdown (que tem z-index 100000) */
.vs-notifications, .el-message {
  z-index: 1000000 !important;
}

.ctl-menu.el-dropdown-menu {
  z-index: 100000 !important; /* acima do vs-sidebar / modais da vuesax */
  min-width: 252px;
  padding: 6px !important;
  border: 1px solid #e5e7eb !important; /* gray-200 */
  border-radius: 12px !important;
  background: #ffffff !important;
  box-shadow: 0 12px 24px -8px rgba(16, 24, 40, 0.18),
    0 4px 8px -4px rgba(16, 24, 40, 0.1) !important;
  margin-top: 6px !important;
}
.ctl-menu.el-dropdown-menu .popper__arrow {
  display: none !important;
}
.ctl-menu .ctl-menu__label {
  padding: 4px 10px 6px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #9ca3af; /* gray-400 */
}
.ctl-menu .ctl-item {
  display: flex;
  align-items: stretch;
  gap: 2px;
  border-radius: 9px;
}
.ctl-menu .ctl-item + .ctl-item {
  margin-top: 2px;
}
.ctl-menu .ctl-item__main {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
  padding: 8px 10px;
  border: 0;
  background: transparent;
  border-radius: 9px;
  cursor: pointer;
  text-align: left;
  font: inherit;
  transition: background-color 0.15s ease;
}
.ctl-menu .ctl-item__main:hover {
  background: #f3f4f6; /* gray-100 */
}
.ctl-menu .ctl-item__main:hover .ctl-item__go {
  opacity: 0.8;
  transform: translate(1px, -1px);
}
.ctl-menu .ctl-item__icon {
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
}
.ctl-menu .ctl-item__icon svg {
  display: block;
}
.ctl-menu .ctl-item__icon--wa {
  background: #e7f8ee;
  color: #25d366; /* WhatsApp */
}
.ctl-menu .ctl-item__icon--wati {
  background: #e6f7f4;
  color: #0d9488; /* teal-600 (atendimento WATI) */
}
.ctl-menu .ctl-item__body {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  flex: 1 1 auto;
}
.ctl-menu .ctl-item__title {
  font-size: 14px;
  font-weight: 600;
  color: #111827; /* gray-900 */
  line-height: 1.2;
}
.ctl-menu .ctl-item__desc {
  font-size: 12px;
  color: #6b7280; /* gray-500 */
  line-height: 1.2;
  margin-top: 2px;
}
.ctl-menu .ctl-item__go {
  flex: 0 0 auto;
  color: #cbd5e1; /*slate-300*/
  opacity: 0;
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.ctl-menu .ctl-copy {
  flex: 0 0 auto;
  width: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  border-radius: 9px;
  cursor: pointer;
  color: #9ca3af; /* gray-400 */
  transition: background-color 0.15s ease, color 0.15s ease,
    transform 0.12s ease;
}
.ctl-menu .ctl-copy:hover {
  background: #eef2ff; /* indigo-50 */
  color: #4f46e5; /* indigo-600 */
}
.ctl-menu .ctl-copy:active {
  transform: scale(0.92);
}
.ctl-menu .ctl-copy.is-copied {
  color: #16a34a; /* green-600 */
  background: #dcfce7; /* green-100 */
}
.ctl-menu .ctl-copy svg {
  display: block;
  animation: ctl-pop 0.18s ease;
}
@keyframes ctl-pop {
  from {
    transform: scale(0.55);
    opacity: 0.3;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
